// Conference room — memory-only join-session loader.
//
// Adapts the REAL server-scoped join API (POST /api/square/daily/meetings/join,
// via api.commandCenter.meetings.join) into the ConferenceRoomGate's
// JoinSessionResult. The short-lived token is fetched on demand and handed to
// ConferenceRoomGate, which holds it only in React state and passes it to the
// in-memory Daily call object. It is NEVER placed in a URL, query string, route
// state, browser storage, log, receipt, or database row. The browser never sees
// DAILY_API_KEY — the token is minted server-side and returned once.

import { api } from '../../../../api/client'
import type { JoinSessionResult } from './ConferenceRoomGate.types'

type JoinResponse = {
  ok?: boolean
  room_url?: string
  token?: string
  expires_at?: string
  reason?: string
  error?: string
}

/**
 * Build the gate's loadJoinSession callback. Closes over the display name and
 * the caller's user id; the token is fetched inside and returned in memory.
 * The AbortSignal is honored by the gate (it discards a late result); the
 * underlying request is a plain authenticated POST.
 *
 * participant_role must be a real value from the server's PARTICIPANT_ROLES
 * enum (api/_lib/meetings/types.ts) — an earlier version sent the literal
 * string 'operator', which is not a member of that set and was silently
 * coerced to 'participant' server-side (api/square/daily/meetings/join.ts),
 * so no caller from this room could ever become the Daily token's is_owner
 * unless their account role level was already >= 80.
 *
 * The fix intentionally does NOT derive the request from the caller's role
 * tier alone (e.g. "is a manager") — role tier and "is this meeting's host"
 * are orthogonal: a manager-tier user joining someone ELSE's meeting is not
 * that meeting's host. issueJoinToken derives is_owner as
 * `participantRole === 'host' || actor.roleLevel >= 80` and
 * decideJoinPermission only rejects a 'host' request below role level 50 —
 * neither checks meeting ownership. So the client asks for 'host' only when
 * it can show the caller actually created this specific meeting (looked up
 * via the same meetings.list() the Conference Calls list already uses);
 * account-level admins still get is_owner via the server's own
 * roleLevel >= 80 disjunct, independent of what this sends.
 */
export function createConferenceJoinLoader(displayName?: string, userId?: string) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars -- required by the JoinSessionResult loader contract (ConferenceRoomGate honors abort itself); api.commandCenter.meetings.join has no signal passthrough today.
  return async (meetingId: string, _signal: AbortSignal): Promise<JoinSessionResult> => {
    let participantRole: 'host' | 'participant' = 'participant'
    if (userId) {
      try {
        const list = await api.commandCenter.meetings.list()
        const meeting = list.meetings.find((m) => m.id === meetingId)
        if (meeting && meeting.created_by === userId) participantRole = 'host'
      } catch {
        // Lookup failed — fall back to 'participant'. Never guess 'host'.
      }
    }
    try {
      const res = (await api.commandCenter.meetings.join(meetingId, participantRole, displayName)) as JoinResponse
      if (!res.ok || !res.room_url || !res.token) {
        return { ok: false, reason: res.reason ?? res.error ?? 'join_failed' }
      }
      return {
        ok: true,
        session: { roomUrl: res.room_url, token: res.token, expiresAt: res.expires_at },
      }
    } catch {
      // Never surface a raw provider/stack error to the room; the gate humanizes.
      return { ok: false, reason: 'join_failed' }
    }
  }
}
